import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ColumnLayout,
  Container,
  SpaceBetween,
  StatusIndicator,
} from '@cloudscape-design/components';
import { getAwsSignoffUrl } from '../services/s3Service';

// The Claim section under the Key Findings: what the agent found on the claim (phase
// evidence, the partner's half of the sign-off, AWS's half) and one button that hands
// the PSA the partner's sign-off. Chronos signs nothing and stores nothing: the button
// asks for APFP's short-lived presigned URL and the browser downloads from APFP.

const STATUS = {
  ready: { type: 'success', label: 'Ready for your AWS sign-off' },
  check: { type: 'warning', label: 'Ready, check before signing' },
  already_signed: { type: 'info', label: 'AWS sign-off already done' },
  wrong_template: { type: 'warning', label: 'Wrong template: ask for the Partner Sign-Off' },
  blocked: { type: 'stopped', label: 'Not ready for AWS sign-off' },
};

const LINE = {
  ok: 'success',
  warn: 'warning',
  info: 'info',
  todo: 'pending',
};

const LABEL = {
  phase_output: 'MVA output',
  rvtools: 'RVtools',
  tags: 'Tags',
  partner_signoff: 'Partner sign-off',
  aws_section: 'AWS section',
};

// The request's own currency and amount, as the page header prints them ("GBP 14,025"):
// the amount arrives as a bare number in the request's currency, never converted.
const amount = (value, currency) => {
  const number = Number(String(value ?? '').replace(/[^0-9.-]/g, ''));
  return value == null || value === '' || Number.isNaN(number)
    ? null
    : `${currency || 'USD'} ${number.toLocaleString('en-US')}`;
};

const date = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { timeZone: 'UTC' }) : '-');

export default function AwsSignoffPanel({ fundRequestId, signoff, requested, currency }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const status = STATUS[signoff.status] || STATUS.blocked;
  const claim = signoff.claim || {};
  const lines = signoff.lines || [];

  const handleDownload = async () => {
    setBusy(true);
    setError(null);
    // Opened inside the click so the browser does not block it as a pop-up.
    const tab = window.open('about:blank', '_blank');
    try {
      const url = await getAwsSignoffUrl(fundRequestId);
      if (tab) {
        // The tab navigates to APFP's storage: cut the link back to this page first.
        tab.opener = null;
        tab.location.href = url;
      } else {
        window.location.href = url;
      }
    } catch (err) {
      if (tab) tab.close();
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Container>
      {/* Same 2-column grid as the Key Findings box above, so both halves of this
          section sit inside its first column. The second column is left empty. */}
      <ColumnLayout columns={2} variant="text-grid">
        <div style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'stretch', gap: 16 }}>
          {/* Left: what the agent found on the claim. Its text wraps; the action
              stays beside it. */}
          <div style={{ flex: '1 1 auto', minWidth: 0 }}>
            <SpaceBetween size="xxxs">
              <Box variant="h4" padding={{ bottom: 'xxs' }}>
                {['Claim', signoff.phase].filter(Boolean).join(' · ')}
                <Box variant="small" color="text-body-secondary" display="inline">
                  {[
                    amount(requested, currency),
                    claim.actual_start || claim.actual_end ? `${date(claim.actual_start)} to ${date(claim.actual_end)}` : null,
                  ].filter(Boolean).map((part) => ` · ${part}`).join('')}
                </Box>
              </Box>
              {lines.map((line) => (
                <div key={line.item}>
                  <StatusIndicator type={LINE[line.state] || 'info'}>
                    <Box variant="span" fontWeight="bold">{LABEL[line.item] || line.item}:</Box> {line.text}
                  </StatusIndicator>
                </div>
              ))}
            </SpaceBetween>
          </div>

          {/* Right: one button, the same in every state, next to the diagnosis. */}
          <div style={{ borderLeft: '1px solid #e9ebed', paddingLeft: 16, flex: '0 0 200px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
            <SpaceBetween size="xxs">
              {signoff.file_id && (
                <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <Button variant="primary" loading={busy} onClick={handleDownload}>
                    Download
                  </Button>
                  <Box variant="small" color="text-body-secondary">
                    {new Date().toLocaleDateString()}
                  </Box>
                </div>
              )}
              {status.type !== 'success' && (
                <StatusIndicator type={status.type}>{status.label}</StatusIndicator>
              )}
              {error && <Alert type="error">{error}</Alert>}
            </SpaceBetween>
          </div>
        </div>
        <div />
      </ColumnLayout>
    </Container>
  );
}
